import { getUserInfo } from "../../../api/axios";
import { WebStorage, WebStorageNames } from "../../../utils";

/** Line manager id from login payload (`ApproverId` / `ApplicationUser.IsManageBy`). */
export const getLineManagerId = (userInfo = getUserInfo()) => {
  if (!userInfo || typeof userInfo !== "object") return "";
  const raw =
    userInfo.approverId ??
    userInfo.ApproverId ??
    userInfo.isManageBy ??
    userInfo.IsManageBy ??
    "";
  return String(raw || "").trim();
};

export const userHasLineManager = (userInfo = getUserInfo()) => Boolean(getLineManagerId(userInfo));

export const getAppraisalRoleId = () => {
  const r = WebStorage.getLocalStore(WebStorageNames.RoleId) ?? localStorage.getItem("roleId");
  return parseInt(String(r), 10) || 0;
};

/**
 * My Appraisal / My PDP / personal appraisal pages are for employees in the manager hierarchy.
 * Company admins (roles 1–2) only see them when they have a line manager assigned.
 */
export const canAccessMyAppraisal = (roleId = getAppraisalRoleId(), userInfo = getUserInfo()) => {
  const rid = parseInt(String(roleId), 10) || 0;
  if (rid === 1 || rid === 2) return userHasLineManager(userInfo);
  return rid === 3 || rid === 4;
};

/** Default landing path inside Appraisal for the current role. */
export const getAppraisalHomePath = (roleId = getAppraisalRoleId(), userInfo = getUserInfo()) => {
  const rid = parseInt(String(roleId), 10) || 0;
  if (rid === 1 || rid === 2 || rid === 3) return "/appraisal/overview";
  if (canAccessMyAppraisal(rid, userInfo)) return "/appraisal/self";
  return "/workplaces";
};

/** Default meaning when the company has not configured custom scale letters. */
const DEFAULT_FIVE = {
  1: "Needs improvement",
  2: "Below expectations",
  3: "Meets expectations",
  4: "Above expectations",
  5: "Outstanding",
};

export const normalizeRatingLabels = (raw) => {
  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((c) => ({
      ratingNo: Number(c.ratingNo ?? c.RatingNo ?? 0),
      letter: String(c.letter ?? c.Letter ?? "").trim(),
    }))
    .filter((c) => c.ratingNo > 0 && c.letter);
};

export const getRatingLabel = (n, max, customLabels = []) => {
  const custom = customLabels.find((c) => c.ratingNo === n);
  if (custom?.letter) return custom.letter;
  if (max === 5 && DEFAULT_FIVE[n]) return DEFAULT_FIVE[n];
  if (n === 1) return "Lowest";
  if (n === max) return "Highest";
  return `Score ${n}`;
};

export const buildRatingGuide = (max, customLabels = []) =>
  Array.from({ length: max }, (_, i) => {
    const n = i + 1;
    return { n, label: getRatingLabel(n, max, customLabels) };
  });
